import React, { useEffect, useState } from "react";

import { notifications } from "@mantine/notifications";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { IconChevronRight, IconFolderFilled, IconSearch } from "@tabler/icons-react";
import { TextInput } from "@mantine/core";
import {
  Button,
  Modal,
  ModalActions,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalTitle,
} from "@onengine/component-library";

import { useFileManager, useFileManagerUI } from "@/components/core/FileManager";
import type { BreadcrumbEntry, FileManagerItem } from "@/components/core/FileManager";
import { modalBody } from "@/components/features/FileManager/Modals/modals.style.ts";
import { useFileManagerMount } from "@/components/features/FileManager/FileManagerMount";

interface UseFolderBrowserOptions {
  enabled: boolean;
  resetKey: string | undefined;
}

function useFolderBrowser({ enabled, resetKey }: UseFolderBrowserOptions) {
  const { browseItems, queryScope } = useFileManagerMount();
  const [browseFolderId, setBrowseFolderId] = useState<string | null>(null);
  const [browseBreadcrumb, setBrowseBreadcrumb] = useState<BreadcrumbEntry[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!enabled) return;
    setBrowseFolderId(null);
    setBrowseBreadcrumb([]);
    setSearch("");
  }, [resetKey, enabled]);

  const [debouncedSearch] = useDebouncedValue(search, 350);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["move-modal-browse", ...queryScope, browseFolderId, debouncedSearch],
    queryFn: () => browseItems(browseFolderId, debouncedSearch || null),
    enabled,
  });

  function navigateInto(folder: FileManagerItem) {
    setBrowseBreadcrumb((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setBrowseFolderId(folder.id);
    setSearch("");
  }

  function navigateTo(index: number) {
    if (index === -1) {
      setBrowseFolderId(null);
      setBrowseBreadcrumb([]);
    } else {
      setBrowseFolderId(browseBreadcrumb[index].id);
      setBrowseBreadcrumb((prev) => prev.slice(0, index + 1));
    }
    setSearch("");
  }

  return {
    browseFolderId,
    browseBreadcrumb,
    search,
    setSearch,
    items,
    isLoading,
    navigateInto,
    navigateTo,
  };
}

const folderRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: 8,
  borderRadius: 4,
  background: "none",
  border: "none",
  cursor: "pointer",
  width: "100%",
  textAlign: "left",
};

const breadcrumbBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontSize: 14,
  color: "#363d4d",
};

const breadcrumbRow: React.CSSProperties = {
  display: "flex",
  gap: 4,
  alignItems: "center",
  flexWrap: "wrap",
};

const folderList: React.CSSProperties = {
  border: "1px solid #e2e6eb",
  borderRadius: 8,
  maxHeight: 280,
  overflowY: "auto",
  paddingBlock: 6,
};

const folderListMessage: React.CSSProperties = {
  margin: 0,
  padding: "8px 12px",
  fontSize: 14,
  color: "#646d7e",
};

const folderRowName: React.CSSProperties = { margin: 0, fontSize: 14, color: "#363d4d" };
const folderRowMeta: React.CSSProperties = { margin: 0, fontSize: 12, color: "#646d7e" };
const folderRowBody: React.CSSProperties = { flex: 1, minWidth: 0 };
const iconShrink: React.CSSProperties = { flexShrink: 0 };

export function MoveModal() {
  const { moveItems } = useFileManager();
  const { moveTarget, closeMove } = useFileManagerUI();

  const {
    browseFolderId,
    browseBreadcrumb,
    search,
    setSearch,
    items,
    isLoading,
    navigateInto,
    navigateTo,
  } = useFolderBrowser({ enabled: !!moveTarget, resetKey: moveTarget?.id });

  const folders = items.filter(
    (item): item is FileManagerItem => item.type === "FOLDER" && item.id !== moveTarget?.id,
  );

  function handleMove() {
    if (!moveTarget) return;
    const destinationName = browseBreadcrumb.at(-1)?.name ?? null;
    moveItems(
      {
        items: [{ type: moveTarget.type, id: moveTarget.id, name: moveTarget.name }],
        destination: { id: browseFolderId, name: destinationName },
      },
      {
        onSuccess: closeMove,
        onError: () => {
          notifications.show({
            title: "Move failed",
            message: "Could not move. Please try again.",
            color: "red",
            autoClose: 4000,
          });
        },
      },
    );
  }

  const moveButtonLabel = moveTarget?.type === "FOLDER" ? "Move Folder" : "Move File";

  return (
    <Modal open={!!moveTarget} onClose={closeMove} size="large">
      <ModalContent>
        <ModalTitle>Move &#34;{moveTarget?.name}&#34;</ModalTitle>
        <ModalBody>
          <div style={modalBody}>
            <TextInput
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
            />

            <div style={breadcrumbRow}>
              <button style={breadcrumbBtn} onClick={() => navigateTo(-1)}>
                Files
              </button>
              {browseBreadcrumb.map((entry, i) => (
                <React.Fragment key={entry.id}>
                  <IconChevronRight size={14} color="#646d7e" />
                  <button style={breadcrumbBtn} onClick={() => navigateTo(i)}>
                    {entry.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            <div style={folderList}>
              {isLoading && <p style={folderListMessage}>Loading...</p>}
              {!isLoading && folders.length === 0 && (
                <p style={folderListMessage}>No folders found</p>
              )}
              {folders.map((folder) => (
                <button key={folder.id} style={folderRow} onClick={() => navigateInto(folder)}>
                  <IconFolderFilled size={20} color="#4C6EF5" style={iconShrink} />
                  <div style={folderRowBody}>
                    <p style={folderRowName}>{folder.name}</p>
                    <p style={folderRowMeta}>
                      Folder &bull; {folder.childCount} {folder.childCount === 1 ? "item" : "items"}
                    </p>
                  </div>
                  <IconChevronRight size={20} color="#646d7e" style={iconShrink} />
                </button>
              ))}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalActions position={"right"}>
            <Button variant="neutral-secondary" onClick={closeMove}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleMove}>
              {moveButtonLabel}
            </Button>
          </ModalActions>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
